import type { TagType } from "@/components/Tag/Tag";
import type { Path } from "@/router";

export type RosterActivityKind =
  | "JOINED_SECTION"
  | "LEFT_SECTION"
  | "CHAPTER_CHANGE"
  | "FACILITY_CHANGE"
  | "TITLE_CHANGE"
  | "EMPLOYMENT_STARTED"
  | "COHORT_CHANGE"
  | "RETIRED"
  | "DECEASED";

export type RosterActivityCohort = "MEMBER" | "ASSOCIATE" | "STUDENT";

export type RosterActivityPersonFields = {
  firstName?: string | null;
  lastName?: string | null;
  primaryFacility?: { name?: string | null } | null;
};

/** Row shape returned by `SECTION_ROSTER_ACTIVITY_QUERY` before client enrichment. */
export type RosterActivityEventFromApi = {
  id: string;
  occurredAt: string;
  kind: RosterActivityKind;
  cohort: RosterActivityCohort;
  headline: string;
  detail?: string | null;
  personName: string;
  pgaId?: string | null;
  /** App route for the person deep link from the API. */
  linkPath?: Path | null;
  person?: RosterActivityPersonFields | null;
};

export type RosterActivityEvent = RosterActivityEventFromApi & {
  /** Populated client-side by `enrichRosterActivityEvent`. */
  firstName?: string;
  lastName?: string;
  facilityName?: string;
};

export const ALL_KINDS: RosterActivityKind[] = [
  "JOINED_SECTION",
  "LEFT_SECTION",
  "CHAPTER_CHANGE",
  "FACILITY_CHANGE",
  "TITLE_CHANGE",
  "EMPLOYMENT_STARTED",
  "COHORT_CHANGE",
  "RETIRED",
  "DECEASED"
];

export const DEFAULT_COHORTS: RosterActivityCohort[] = ["MEMBER", "ASSOCIATE", "STUDENT"];

export const KIND_LABELS: Record<RosterActivityKind, string> = {
  JOINED_SECTION: "Joined section",
  LEFT_SECTION: "Left section",
  CHAPTER_CHANGE: "Chapter change",
  FACILITY_CHANGE: "Facility change",
  TITLE_CHANGE: "Title change",
  EMPLOYMENT_STARTED: "New employment",
  COHORT_CHANGE: "Program change",
  RETIRED: "Retirement",
  DECEASED: "In Memoriam"
};

export const COHORT_LABELS: Record<RosterActivityCohort, string> = {
  MEMBER: "Member",
  ASSOCIATE: "Associate",
  STUDENT: "Student"
};

/** Closed MultiSelect summary: top item by count, or that item plus remaining selected. */
function summarizeSelection<T extends string>(
  selected: T[],
  counts: Record<T, number>,
  labels: Record<T, string>,
  order: readonly T[]
): string | undefined {
  if (selected.length === 0) return undefined;
  if (selected.length === 1) return labels[selected[0]];

  const top = selected.reduce((best, value) => {
    const countDiff = counts[value] - counts[best];
    if (countDiff > 0) return value;
    if (countDiff < 0) return best;
    return order.indexOf(value) < order.indexOf(best) ? value : best;
  });

  const otherCount = selected.length - 1;
  return `${labels[top]} + ${otherCount} ${otherCount === 1 ? "other" : "others"}`;
}

/** Closed MultiSelect summary: top kind by count, or that kind plus remaining selected. */
export function summarizeSelectedKinds(
  selected: RosterActivityKind[],
  counts: Record<RosterActivityKind, number>
): string | undefined {
  return summarizeSelection(selected, counts, KIND_LABELS, ALL_KINDS);
}

export type EventGroupKey = "changes" | "retirements" | "inMemoriam";

export const EVENT_GROUPS: { key: EventGroupKey; title: string; kinds: RosterActivityKind[] }[] = [
  {
    key: "changes",
    title: "Changes",
    kinds: [
      "JOINED_SECTION",
      "LEFT_SECTION",
      "CHAPTER_CHANGE",
      "FACILITY_CHANGE",
      "TITLE_CHANGE",
      "EMPLOYMENT_STARTED",
      "COHORT_CHANGE"
    ]
  },
  {
    key: "retirements",
    title: "Retirements",
    kinds: ["RETIRED"]
  },
  {
    key: "inMemoriam",
    title: "In Memoriam",
    kinds: ["DECEASED"]
  }
];

export type RosterMovementBand = "joined" | "left" | "inMemoriam";

export type RosterMovementRow = {
  id: string;
  initials: string;
  name: string;
  subtitle: string;
  statusLabel: string;
  statusTagType: TagType;
  occurredAt: string;
  pgaId?: string | null;
  linkPath?: Path | null;
  band: RosterMovementBand;
  sourceEvent: RosterActivityEvent;
};
